/**
 * Admin Job Management - Job management with filters
 */
import { useLanguage } from "@/context/AppContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronLeft, Search, Briefcase, Trash2, Eye, Loader2, MapPin, Calendar, DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminJobManagement = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchJobs();
  }, [page, statusFilter]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/jobs?page=${page}&limit=20`;
      if (searchQuery) url += `&search=${searchQuery}`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      const res = await axios.get(url, { withCredentials: true });
      setJobs(res.data.jobs || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      toast.error('Fehler beim Laden der Aufträge');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchJobs();
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Auftrag löschen möchten?')) return;
    
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, { withCredentials: true });
      toast.success('Auftrag gelöscht');
      fetchJobs();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'completed': return 'bg-blue-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Offen';
      case 'in_progress': return 'In Bearbeitung';
      case 'completed': return 'Abgeschlossen';
      case 'cancelled': return 'Abgebrochen';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen infirmly-dark bg-gray-100" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }} data-testid="admin-job-management">
      {/* Header */}
      <header className="border-b border-white/10 sticky bg-white/[0.04] backdrop-blur top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="rounded-full">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('adminPageJobMgmt')}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Titel oder Kategorie suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 rounded-full"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48 rounded-full">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="open">Offen</SelectItem>
                  <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                  <SelectItem value="completed">Abgeschlossen</SelectItem>
                  <SelectItem value="cancelled">Abgebrochen</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} className="bg-[#0044CC] hover:bg-[#003399] rounded-full">
                Suchen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Job List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Aufträge ({jobs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Keine Aufträge gefunden
              </div>
            ) : (
              <div className="divide-y">
                {jobs.map((job) => (
                  <div key={job.job_id} className="py-4 flex items-center justify-between hover:bg-gray-50 px-2 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-medium">{job.title}</p>
                        <Badge className={getStatusColor(job.status)}>
                          {getStatusLabel(job.status)}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-3 h-3" /> {job.category}
                        </span>
                        {job.location?.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {job.location.city}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" /> CHF {job.budget}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {new Date(job.created_at).toLocaleDateString('de-CH')}
                        </span>
                      </div>
                      {/* Client Info */}
                      <div className="mt-2 text-sm">
                        <span className="text-gray-500">Auftraggeber: </span>
                        <span className="font-medium">{job.client_name}</span>
                        {job.client_email && (
                          <span className="text-gray-400 ml-2">({job.client_email})</span>
                        )}
                      </div>
                      {job.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{job.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Badge variant="outline">{job.bids_count || 0} Gebote</Badge>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/dashboard/jobs/${job.job_id}`)}
                        className="rounded-full"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => deleteJob(job.job_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button 
                  variant="outline" 
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="rounded-full"
                >
                  Zurück
                </Button>
                <span className="flex items-center px-4">
                  Seite {page} von {totalPages}
                </span>
                <Button 
                  variant="outline" 
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="rounded-full"
                >
                  Weiter
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminJobManagement;
